import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import { api, formatError } from "@/lib/api";

export const Recommender = () => {
  const [form, setForm] = useState({ name: "", contact: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact || !form.description) {
      toast.error("Preencha todos os campos.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/orders", form);
      setResult(data);
      toast.success("Pedido enviado! Veja abaixo a recomendação da nossa IA.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-ink border border-white/15 px-4 py-4 text-white placeholder:text-white/30 font-sans focus:outline-none focus:border-cobalt transition-colors duration-300";

  return (
    <section id="recomendador" className="py-24 md:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <div className="mb-6">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-acid">Recomendador IA</span>
            </div>
            <h2 className="font-head font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-6">
              Seu app,<br />suas regras.
            </h2>
            <p className="text-white/55 text-lg leading-relaxed max-w-md">
              Descreva o que o seu negócio precisa. Nossa IA analisa o pedido e apresenta o
              <span className="text-white"> melhor app</span>, pronto ou sob medida, em segundos.
              Seu pedido chega direto para a nossa equipe.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="space-y-5 border border-white/10 p-6 md:p-10 bg-surface/40" data-testid="recommender-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-2 block">Nome</label>
                <input data-testid="form-name" className={field} placeholder="Seu nome ou empresa"
                       value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-2 block">Contato</label>
                <input data-testid="form-contact" className={field} placeholder="WhatsApp ou e-mail"
                       value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-2 block">O que você precisa?</label>
              <textarea data-testid="form-description" rows={5} className={field}
                        placeholder="Ex: preciso controlar as vendas e o estoque da minha loja de roupas..."
                        value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <button type="submit" disabled={loading} data-testid="form-submit"
                    className="group w-full inline-flex items-center justify-center gap-3 bg-cobalt text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-ink transition-colors duration-300 disabled:opacity-60">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analisando seu pedido...</>
                       : <><Sparkles className="w-4 h-4" /> Descobrir meu app ideal</>}
            </button>

            <AnimatePresence>
              {result && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0 }}
                  className="overflow-hidden" data-testid="recommendation-result">
                  <div className="mt-4 border border-cobalt/40 bg-cobalt/5 p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle2 className="w-5 h-5 text-acid" />
                      <span className="font-mono text-xs uppercase tracking-[0.2em] text-acid">Recomendação da IA</span>
                    </div>
                    <h3 className="font-head font-bold text-2xl md:text-3xl mb-1">{result.recommended_app}</h3>
                    <p className="font-mono text-cobalt text-sm mb-4">{result.recommended_plan}</p>
                    <p className="text-white/70 leading-relaxed">{result.reasoning}</p>
                    <a href="https://wa.me/5542999587464" target="_blank" rel="noreferrer"
                       className="inline-block mt-5 font-mono text-xs uppercase tracking-[0.2em] px-6 py-3 border border-white/20 hover:bg-white hover:text-ink transition-colors duration-300">
                      Falar com a equipe →
                    </a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>
      </div>
    </section>
  );
};
